/**
 * React server side rendering shared functionality
 * @module react-universal/server
 */
/* eslint-disable react/jsx-filename-extension */
import React from 'react'
import { hydrateRoot } from 'react-dom/client'
import { getAppData } from 'react-universal/browser'
import _get from 'lodash/get'
import _isUndefined from 'lodash/isUndefined'
import _isEqual from 'lodash/isEqual'
import { get as getCookie } from 'cookies-js'
import { get as getConfig } from 'config'
import { setLogger, getLogger } from 'log'
import { getLogger as getLoggerBrowser } from 'log/browser'
import { createStore } from 'store/configureStore'
import { initialState } from 'reducers'
import { AUTH_COOKIE_NAME, setTokenDataInStorage } from 'services/auth'
import { getResolverData, setResolverLocation } from 'services/resolver/actions'
import { getViewport } from 'services/app/_viewport'
import { createHistory, parseLocation } from 'services/resolver'
import { isAnonymousHome, isLogout } from 'services/url'
import App from 'components/App'
import { getRoutes } from 'routes'
import { getAnonymousUuid } from './browser/anonymous'
import { init as initNewRelic } from './browser/new-relic'
import { LOCAL } from './environment'

/**
 * Import CSS files
 */
async function importCss () {
  if (!process.env.BROWSER) {
    return
  }
  await import(/* webpackChunkName: 'styles' */ './theme/web-app/_web-app.scss')
  // Third party CSS
  // await import(/* webpackChunkName: 'styles' */ 'react-selectize/themes/index.css')
}

const config = getConfig()
const ENV_LOCAL = !process.env.NODE_ENV || process.env.NODE_ENV === LOCAL

/**
 * Reload the page when the data is not syned between
 * the hydrated data and the cookies to prevent inconsitent
 * behavior in the application
 */
function reloadNotSynced () {
  // Don't block the logout page.
  if (isLogout(
    _get(window, ['location', 'pathname']),
  )) {
    return
  }

  try {
    const appData = getAppData()
    const authCookie = getCookie(AUTH_COOKIE_NAME)
    const auth = authCookie ? JSON.parse(authCookie) : {}
    const appAuth = _get(appData, ['hydrate', 'auth'])
    const appJwt = _get(appAuth, ['jwt'])
    const cookieJwt = _get(auth, 'jwt')
    if (cookieJwt !== appJwt) {
      // Write the app auth to the cookies
      // which will also clean duplicates
      setTokenDataInStorage(appAuth)
      window.location.reload()
    }
  } catch (err) {
    const log = getLogger()
    log.error(err, 'Failed parse auth cookie data in reloadNotSynced')
  }
}

/**
 * Initialize a buyan logger and set it in the library
 * as a singleton
 */
function initLogger () {
  const appData = getAppData()
  const uid = _get(appData, ['hydrate', 'auth', 'uid'])
  const logger = getLoggerBrowser(uid)
  setLogger(logger)
  return logger
}

function renderApp (options = {}) {
  const { store, history } = options
  return (
    <App
      store={store}
      history={history}
      location={global.location}
      key={!_isUndefined(module.hot) ? Math.random() : undefined}
    />
  )
}

/**
 * Attach a listener to the history object which updates the
 * which dispatches actions resolver actions to redux
 * @param {object} options The options
 * @param {object} options.store A redux store
 * @param {object} options.location The location object from a history api event
 */
function attachResolverHistoryListener ({ store, location }) {
  // Only apply marketing layer proxy to the anonymous home page
  // When you are logged in and the feature is enabled
  const { pathname } = location
  const { auth } = store.getState()
  const jwt = auth.get('jwt')
  if (_get(config, 'features.marketing.proxyAnonymousHome') && isAnonymousHome(pathname, jwt)) {
    window.location.reload()
  }
  const { resolver } = store.getState()
  // location is an object like window.location
  if (pathname !== resolver.get('path')) {
    const routes = getRoutes()
    store.dispatch(getResolverData(location, routes))
  } else if (!_isEqual(location, resolver.get('location'))) {
    // Have have already resolved the path so just update the location
    store.dispatch(setResolverLocation(location))
  }
}

/**
 * The entry point for the browser code in the Web App.
 */
export async function run () {
  const {
    log: {
      browser: {
        level: logLevel,
      } = {},
    } = {},
  } = config
  const startTime = new Date().getTime()
  const logger = initLogger()

  function logBrowserError (error, name) {
    logger.error(error, `Browser run failed for ${name}`)
  }

  try {
    window.addEventListener('unhandledrejection', (e) => {
      e.preventDefault()
      const reason = _get(e, ['reason'])
      const detailReason = _get(e, ['detail', 'reason'])
      const log = logger || console
      log.error(detailReason || reason || 'Unknown reason in unhandledrejection')
    })

    initNewRelic({ appData: getAppData(), config })
    reloadNotSynced()
    importCss()

    const appData = getAppData()
    const history = createHistory()
    const hydrate = _get(appData, 'hydrate', {})
    const isWebView = _get(hydrate, 'app.isWebView', false)
    const rawAuth = _get(hydrate, 'auth')
    const historyLocation = parseLocation(_get(history, 'location', {}))

    hydrate.app.client = true
    hydrate.app.server = false
    hydrate.app.bootstrapPhase = null
    hydrate.app.viewport = getViewport()
    hydrate.app.bootstrapComplete = false
    hydrate.app.initializationStartTime = startTime

    hydrate.app.renderFooter = !rawAuth && !isWebView
    hydrate.app.renderHeader = !rawAuth && !isWebView
    hydrate.app.renderApp = !rawAuth

    if (!rawAuth) {
      const uuid = await getAnonymousUuid()
      hydrate.user = hydrate.user || {}
      hydrate.user.data = hydrate.user.data || {}
      hydrate.user.data.anonymousUuid = uuid
    }

    /**
     * The location information for the resolver needs to be rebuilt here due to caching issues,
     * primarily with query parameters. If a page is cached by the server, then the server side
     * hydration will contain the incorrect query parameters. This will now use the location
     * parameters provided by the client on app bootstrap.
     */
    hydrate.resolver.location = historyLocation
    hydrate.resolver.path = _get(historyLocation, 'pathname', '')
    hydrate.resolver.query = _get(historyLocation, 'query', {})
    hydrate.resolver.processing = false

    const store = await createStore({
      initialState, logLevel, hydrate, history,
    })

    history.listen((location) => (
      attachResolverHistoryListener({ store, location })
    ))

    try {
      const appElement = document.getElementById('app')
      const component = renderApp({ store, history })

      hydrateRoot(appElement, component)

      if (ENV_LOCAL && module.hot) {
        module.hot.accept([
          'reducers',
          'store/configureStore',
          'components/App',
        ], () => {
          renderApp({ store, history })
        })
      }
    } catch (err) {
      logBrowserError(err, 'app')
    }
  } catch (e) {
    logger.error(e)
  }
}

export default run
